import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { api, ApiError, type LlmModel, type Plan } from "../api.js";
import { useToken } from "../auth.js";
import { GroupButton } from "../components/GroupButton.js";

// Plans page — one row per plan (legacy + custom), inline deactivate/reactivate,
// "New plan" modal. Deactivate is refused by the API when customers are still attached;
// reactivate is refused if the linked model is currently inactive. We surface the API's
// 409 message directly to the operator.
export function Plans() {
  const token = useToken();
  const { t } = useLingui();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [models, setModels] = useState<LlmModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, m] = await Promise.all([api.listPlans(token), api.listLlmModels(token)]);
      setPlans(p.plans);
      setModels(m.models);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const modelById = useMemo(() => new Map(models.map((m) => [m.id, m])), [models]);

  const deactivate = async (p: Plan) => {
    if (!confirm(t`Deactivate plan "${p.code}"? Customers attached to it must be reassigned first.`))
      return;
    setBusyId(p.id);
    try {
      await api.deactivatePlan(token, p.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const reactivate = async (p: Plan) => {
    if (!confirm(t`Reactivate plan "${p.code}"?`)) return;
    setBusyId(p.id);
    try {
      await api.reactivatePlan(token, p.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const activePlans = useMemo(() => plans.filter((p) => p.isActive), [plans]);
  const inactivePlans = useMemo(() => plans.filter((p) => !p.isActive), [plans]);
  const usableModels = useMemo(() => models.filter((m) => m.isActive), [models]);

  const renderRow = (p: Plan) => {
    const m = modelById.get(p.modelId);
    // Shown inline rather than as a ninth column — it's an exception, only
    // worth the reader's attention when it differs from the chat model.
    const titleModel = p.titleModelId ? modelById.get(p.titleModelId) : undefined;
    const rewriteModel = p.rewriteModelId ? modelById.get(p.rewriteModelId) : undefined;
    return (
      <tr key={p.id}>
        <td>
          <code>{p.code}</code>
        </td>
        <td>{p.name}</td>
        <td>
          {m ? `${m.provider} · ${m.displayName}` : <span className="muted">?</span>}
          {titleModel && (
            <div className="muted">
              <Trans>titles: {titleModel.displayName}</Trans>
            </div>
          )}
          {rewriteModel && (
            <div className="muted">
              <Trans>rewrite: {rewriteModel.displayName}</Trans>
            </div>
          )}
        </td>
        <td>${Number(p.monthlyBudgetUsd).toFixed(2)}</td>
        <td>{Number(p.tolerancePct).toFixed(0)}%</td>
        <td>{t`${p.retentionDays}d`}</td>
        <td>
          <GroupButton
            label={<Trans>Edit</Trans>}
            dropdownLabel={t`Plan actions`}
            onClick={() => setEditing(p)}
          >
            <button className="link-btn" onClick={() => setEditing(p)}>
              <Trans>Edit</Trans>
            </button>
            {p.isActive ? (
              <button
                className="link-btn danger"
                disabled={busyId === p.id}
                onClick={() => void deactivate(p)}
              >
                <Trans>Deactivate</Trans>
              </button>
            ) : (
              <button
                className="link-btn"
                disabled={busyId === p.id}
                onClick={() => void reactivate(p)}
              >
                <Trans>Reactivate</Trans>
              </button>
            )}
          </GroupButton>
        </td>
      </tr>
    );
  };

  const renderTable = (rows: Plan[]) => (
    <table className="table">
      <thead>
        <tr>
          <th><Trans>Code</Trans></th>
          <th><Trans>Name</Trans></th>
          <th><Trans>Model</Trans></th>
          <th><Trans>Budget / month</Trans></th>
          <th><Trans>Tolerance</Trans></th>
          <th><Trans>Retention</Trans></th>
          <th />
        </tr>
      </thead>
      <tbody>{rows.map(renderRow)}</tbody>
    </table>
  );

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Plans</Trans></h1>
        <button onClick={() => setCreating(true)}><Trans>New plan</Trans></button>
      </header>
      {error && <div className="banner err">{error}</div>}
      {loading ? (
        <p className="muted"><Trans>Loading…</Trans></p>
      ) : (
        <>
          {renderTable(activePlans)}
          {inactivePlans.length > 0 && (
            <>
              <h2 style={{ marginTop: 24 }}><Trans>Inactive plans</Trans></h2>
              {renderTable(inactivePlans)}
            </>
          )}
        </>
      )}

      {creating && (
        <PlanEditor
          models={usableModels}
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void refresh();
          }}
        />
      )}
      {editing && (
        <PlanEditor
          plan={editing}
          models={usableModels}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function PlanEditor({
  plan,
  models,
  onCancel,
  onSaved,
}: {
  plan?: Plan;
  models: LlmModel[];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  const { t } = useLingui();
  const isEdit = !!plan;
  const [code, setCode] = useState(plan?.code ?? "");
  const [name, setName] = useState(plan?.name ?? "");
  const [modelId, setModelId] = useState(plan?.modelId ?? models[0]?.id ?? "");
  // "" in the select means "same as the chat model" and is sent as null.
  const [titleModelId, setTitleModelId] = useState(plan?.titleModelId ?? "");
  // "" here means OFF, not "same as the chat model" — see the field help.
  const [guidanceMode, setGuidanceMode] = useState<"legacy" | "skills-v1">(plan?.guidanceMode ?? "legacy");
  const [rewriteModelId, setRewriteModelId] = useState(plan?.rewriteModelId ?? "");
  const [monthlyBudgetUsd, setMonthlyBudgetUsd] = useState<number>(
    plan ? Number(plan.monthlyBudgetUsd) : 100,
  );
  const [tolerancePct, setTolerancePct] = useState<number>(
    plan ? Number(plan.tolerancePct) : 5,
  );
  const [maxToolCallsPerTurn, setMaxToolCallsPerTurn] = useState(
    plan?.maxToolCallsPerTurn ?? 25,
  );
  const [retentionDays, setRetentionDays] = useState(plan?.retentionDays ?? 90);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!modelId) {
      setError(t`Select a model first`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        await api.updatePlan(token, plan!.id, {
          name,
          modelId,
          titleModelId: titleModelId || null,
          rewriteModelId: rewriteModelId || null,
          guidanceMode,
          monthlyBudgetUsd,
          tolerancePct,
          maxToolCallsPerTurn,
          retentionDays,
        });
      } else {
        await api.createPlan(token, {
          code,
          name,
          modelId,
          titleModelId: titleModelId || null,
          rewriteModelId: rewriteModelId || null,
          guidanceMode,
          monthlyBudgetUsd,
          tolerancePct,
          maxToolCallsPerTurn,
          retentionDays,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? <Trans>Edit plan</Trans> : <Trans>New plan</Trans>}</h2>
        <form onSubmit={submit}>
          <label>
            <Trans>Code</Trans>
            <input
              required
              value={code}
              disabled={isEdit}
              onChange={(e) => setCode(e.target.value)}
              placeholder="chat-pro-2026"
            />
          </label>
          <label>
            <Trans>Name</Trans>
            <input required value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            <Trans>Model</Trans>
            <select value={modelId} onChange={(e) => setModelId(e.target.value)} required>
              <option value="">{t`— select —`}</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.provider} · {m.displayName}
                </option>
              ))}
            </select>
          </label>
          <label>
            <Trans>Title model</Trans>
            <select
              value={titleModelId}
              onChange={(e) => setTitleModelId(e.target.value)}
            >
              <option value="">{t`— same as the chat model —`}</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.provider} · {m.displayName}
                </option>
              ))}
            </select>
            <small>
              <Trans>
                Used only to name a new conversation, once per conversation.
                Pick the cheapest active model — the task needs no tools and no
                reasoning.
              </Trans>
            </small>
          </label>
          <label>
            <Trans>Question rewrite model</Trans>
            <select
              value={rewriteModelId}
              onChange={(e) => setRewriteModelId(e.target.value)}
            >
              <option value="">{t`— off —`}</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.provider} · {m.displayName}
                </option>
              ))}
            </select>
            <small>
              <Trans>
                Rewrites the user's question into the English accounting
                vocabulary of the Sage schema before the database discovery
                tools search it — the metadata is in English and the questions
                are not. Cheapest active model; leaving it off falls back to the
                built-in French lexicon. Billed to Jalfa, not to the customer
                (Usage → platform spend).
              </Trans>
            </small>
          </label>
          <label>
            <Trans>Guidance mode</Trans>
            <select value={guidanceMode} onChange={(e) => setGuidanceMode(e.target.value as "legacy" | "skills-v1")}>
              <option value="legacy">legacy</option>
              <option value="skills-v1">skills-v1</option>
            </select>
          </label>
          <label>
            <Trans>Monthly budget (USD)</Trans>
            <input
              type="number"
              min={0}
              step={1}
              value={monthlyBudgetUsd}
              onChange={(e) => setMonthlyBudgetUsd(Number(e.target.value) || 0)}
              required
            />
          </label>
          <label>
            <Trans>Tolerance (%)</Trans>
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={tolerancePct}
              onChange={(e) => setTolerancePct(Number(e.target.value) || 0)}
            />
          </label>
          <label>
            <Trans>Max tool calls per turn</Trans>
            <input
              type="number"
              min={0}
              value={maxToolCallsPerTurn}
              onChange={(e) => setMaxToolCallsPerTurn(Number(e.target.value) || 0)}
            />
          </label>
          <label>
            <Trans>Retention (days)</Trans>
            <input
              type="number"
              min={1}
              value={retentionDays}
              onChange={(e) => setRetentionDays(Number(e.target.value) || 1)}
            />
          </label>
          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
